import { useQuery } from '@tanstack/react-query';
import { Skeleton, Tag, Typography } from '@volvo/vcdk-react';
import React from 'react';

import { withComponentErrorBoundary } from '@/components/ComponentErrorBoundary';
import { getIndividualProductsQueryOptions } from '@/service/vpc/queryOptions';
import { common } from '@/utils';

type Props = {
  serialNumbers: string[];
};

const ProductDesignation: React.FC<Props> = ({ serialNumbers }) => {
  const { data: unitsProducts, isLoading: isLoadingProducts } = useQuery(
    getIndividualProductsQueryOptions({ serialNumbers })
  );

  const getProductDesignation = (serialNumber: string) => {
    const enginesAndMotors = ['Engine', 'Generator', 'Outboard', 'ElectricMotor'];

    const product = unitsProducts
      ?.filter((p) => enginesAndMotors.includes(p.productCategory))
      .find((p) => p.serialNumber === serialNumber);
    return product?.productDesignation || '';
  };

  const designations = serialNumbers.map((sn) => getProductDesignation(sn)).filter(Boolean);

  // Group designations by count
  const designationCounts = designations.reduce(
    (acc, designation) => {
      acc[designation] = (acc[designation] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  // Get unique designations in original order
  const uniqueDesignations = Array.from(new Set(designations));

  if (isLoadingProducts) {
    return <Skeleton variant="rounded-rectangle" height={18} width={100} />;
  }

  return (
    <div className="flex flex-col gap-2">
      {uniqueDesignations.map((designation) => (
        <div key={common.slugify(designation)} className="flex flex-nowrap items-center gap-3 md:flex-row">
          <Typography variant="caption1" className="text-subtle whitespace-nowrap">
            {designation}
          </Typography>
          {designationCounts[designation] > 1 && (
            <Tag variant="neutral-subtle" size="small">
              x{designationCounts[designation]}
            </Tag>
          )}
        </div>
      ))}
    </div>
  );
};

export default withComponentErrorBoundary(ProductDesignation);
